import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import Typography from '@/components/base/Typography'

import { cardSliderRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type CardSliderProps = HTMLAttributes<HTMLDivElement> & {
  title: string
  subtitle: string
  cards: {
    card_clone: {
      icon: WPImage
      overline: string
      title: string
      text: string
    }
  }[]
}

const CardSlider = ({ title, subtitle, cards, ...props }: CardSliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true
  })
  const [scrollProgress, setScrollProgress] = useState(0)

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 100)
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onScroll(emblaApi)
    emblaApi.on('reInit', onScroll).on('scroll', onScroll).on('slideFocus', onScroll)
  }, [emblaApi, onScroll])

  return (
    <Motion animation={cardSliderRevealAnimation}>
      <Section {...props}>
        <Container>
          <Grid>
            <div
              className={cn(
                'col-span-4 flex flex-col gap-spacing-sm pb-spacing-md md:col-span-6 lg:col-span-4'
              )}
            >
              <Typography variant="h5">{title}</Typography>
              <Typography variant="large" className="font-medium text-text-accent-light-dark">
                {subtitle}
              </Typography>
            </div>
            <div
              className={cn(
                'col-span-4 -mx-[var(--margin)] md:col-span-6 md:-ml-[var(--margin)] lg:col-span-8 lg:ml-0'
              )}
            >
              <section className="embla">
                <div
                  className="embla__viewport px-[var(--margin)] md:pl-[var(--margin)] lg:px-0 lg:pr-[var(--margin)]"
                  ref={emblaRef}
                >
                  <div className="embla__container">
                    {cards.map((card, index) => (
                      <div className="embla__slide" key={index}>
                        <div className="flex h-full flex-col gap-spacing-sm">
                          {card.card_clone?.icon?.url ? (
                            <div
                              className={
                                'flex aspect-[0.87] items-center justify-center bg-gradient-to-b from-[#545454] to-[#303030] p-[1px]'
                              }
                            >
                              {card.card_clone?.icon?.url ? (
                                <div className="flex h-full w-full items-center justify-center bg-[#292929] p-[10px]">
                                  <img
                                    src={card.card_clone.icon.url}
                                    alt={card.card_clone.icon.alt}
                                  />
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="flex flex-grow flex-col gap-spacing-sm">
                            <Typography variant="large" className="font-medium">
                              {card.card_clone.title}
                            </Typography>
                            <Typography variant="regular" className="text-text-accent-light-dark">
                              {card.card_clone.text}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="embla__controls flex w-full px-[var(--margin)] pt-spacing-md lg:pl-0">
                  <div className="size-[2px] bg-white-dark" />
                  <div className="embla__progress relative h-[2px] w-full overflow-hidden bg-white-dark/10">
                    <div
                      className="embla__progress__bar left absolute -left-full bottom-0 top-0 w-full bg-white-dark"
                      style={{ transform: `translate3d(${scrollProgress}%,0px,0px)` }}
                    />
                  </div>
                  <div className="size-[2px] bg-white-dark" />
                </div>
              </section>
            </div>
          </Grid>
        </Container>
      </Section>
    </Motion>
  )
}

export default CardSlider
